// Home and routine (brief 1E-17; DECISIONS 10/28; mockup v76): the patient's photo, the guide to
// the home and the routine by the company's sections, conversation starters, and Before you start in
// the field app. Pure: no database, no clock, no storage.

import { addDays } from "../../../rules/week";

type Read<T> = { ok: true; value: T } | { ok: false; error: string };
const no = (error: string): { ok: false; error: string } => ({ ok: false, error });

// ---- The setting ---------------------------------------------------------------------------------------------

export interface HomeRoutineSetting {
  /** Before you start shows on a first visit, or after this many days away. */
  firstVisitDays: number;
  /** The nurse reviews each guide this many days after its last change. */
  reviewDays: number;
  /** A patient's photo needs photo consent first. */
  photoConsentRequired: boolean;
  /** An orientation visit's checklist is built from the guide; both people check (brief 1E-18). */
  orientationChecklist: boolean;
  /** Running late?: offer a text to the patient once the visit is late (brief 1E-18); late is
   * Scheduling's Late after minutes (brief 1E-19). */
  runningLate: boolean;
}

export const HOME_ROUTINE_DEFAULTS: HomeRoutineSetting = {
  firstVisitDays: 30,
  reviewDays: 90,
  photoConsentRequired: true,
  orientationChecklist: true,
  runningLate: true,
};

const days = (v: unknown, lo: number, hi: number): number | null => {
  const n = typeof v === "string" && v.trim() ? Number(v) : v;
  return typeof n === "number" && Number.isInteger(n) && n >= lo && n <= hi ? n : null;
};

/** The homeRoutine part of the home_care setting, with the defaults for anything missing. */
export function homeRoutineOf(homeCare: unknown): HomeRoutineSetting {
  const s =
    typeof homeCare === "object" && homeCare !== null
      ? ((homeCare as Record<string, unknown>).homeRoutine as Record<string, unknown> | undefined)
      : undefined;
  if (!s || typeof s !== "object") return { ...HOME_ROUTINE_DEFAULTS };
  return {
    firstVisitDays: days(s.firstVisitDays, 1, 365) ?? HOME_ROUTINE_DEFAULTS.firstVisitDays,
    reviewDays: days(s.reviewDays, 1, 730) ?? HOME_ROUTINE_DEFAULTS.reviewDays,
    photoConsentRequired:
      typeof s.photoConsentRequired === "boolean"
        ? s.photoConsentRequired
        : HOME_ROUTINE_DEFAULTS.photoConsentRequired,
    orientationChecklist:
      typeof s.orientationChecklist === "boolean"
        ? s.orientationChecklist
        : HOME_ROUTINE_DEFAULTS.orientationChecklist,
    runningLate:
      typeof s.runningLate === "boolean" ? s.runningLate : HOME_ROUTINE_DEFAULTS.runningLate,
  };
}

/** Reads a change to the setting: what's given replaces what was. */
export function readHomeRoutineSetting(
  input: unknown,
  had: HomeRoutineSetting,
): Read<HomeRoutineSetting> {
  const b = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  const out = { ...had };
  if (b.firstVisitDays !== undefined) {
    const n = days(b.firstVisitDays, 1, 365);
    if (n === null) return no("Type the days for Before you start, 1 to 365.");
    out.firstVisitDays = n;
  }
  if (b.reviewDays !== undefined) {
    const n = days(b.reviewDays, 1, 730);
    if (n === null) return no("Type the days between reviews, 1 to 730.");
    out.reviewDays = n;
  }
  if (b.photoConsentRequired !== undefined) {
    if (typeof b.photoConsentRequired !== "boolean") return no("Say whether consent comes first.");
    out.photoConsentRequired = b.photoConsentRequired;
  }
  if (b.orientationChecklist !== undefined) {
    if (typeof b.orientationChecklist !== "boolean")
      return no("Say whether orientation uses the checklist.");
    out.orientationChecklist = b.orientationChecklist;
  }
  if (b.runningLate !== undefined) {
    if (typeof b.runningLate !== "boolean") return no("Say whether Running late? is offered.");
    out.runningLate = b.runningLate;
  }
  return { ok: true, value: out };
}

// ---- Sections, items and conversation starters ---------------------------------------------------------------

export const ITEM_MAX = 200;
export const TALK_MAX = 40;
export const SECTION_MAX = 40;

const words = (v: unknown) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");

/** A new section's name. */
export function readSectionName(input: unknown, taken: readonly string[]): Read<string> {
  const name = words(input);
  if (!name) return no("Type the section's name.");
  if (name.length > SECTION_MAX) return no(`A section's name is up to ${SECTION_MAX} characters.`);
  if (taken.some((t) => t.trim().toLowerCase() === name.toLowerCase()))
    return no("There's a section with that name already.");
  return { ok: true, value: name };
}

export interface ItemInput {
  sectionId: string;
  body: string;
  photoFileId: string | null;
}

/** An item as Add or the pencil sends it: a section of the company's, the line, a photo or none. */
export function readItem(
  input: unknown,
  sectionIds: readonly string[],
  photoIds: readonly string[],
): Read<ItemInput> {
  const b = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  if (typeof b.sectionId !== "string" || !sectionIds.includes(b.sectionId))
    return no("Pick a section.");
  const body = words(b.body);
  if (!body) return no("Type what to know.");
  if (body.length > ITEM_MAX) return no(`Keep it to ${ITEM_MAX} characters.`);
  const photo = b.photoFileId ?? null;
  if (photo !== null && (typeof photo !== "string" || !photoIds.includes(photo)))
    return no("That photo isn't one of this patient's.");
  return { ok: true, value: { sectionId: b.sectionId, body, photoFileId: photo } };
}

/** One conversation starter. */
export function readTalk(input: unknown): Read<string> {
  const body = words(input);
  if (!body) return no("Type a conversation starter.");
  if (body.length > TALK_MAX) return no(`Keep it short: up to ${TALK_MAX} characters.`);
  return { ok: true, value: body };
}

// ---- Versions ------------------------------------------------------------------------------------------------

export interface ItemLike {
  id: string;
  section_id: string;
  body: string;
  photo_file_id: string | null;
  version: number;
  ended_version: number | null;
}

/** The items a version of the guide holds: added by then, not yet changed or taken out. */
export function guideAt<T extends Pick<ItemLike, "version" | "ended_version">>(
  items: readonly T[],
  version: number,
): T[] {
  return items.filter(
    (i) => i.version <= version && (i.ended_version === null || i.ended_version > version),
  );
}

export type GuideChange =
  { kind: "add"; item: ItemInput } | { kind: "change"; itemId: string; item: ItemInput };

/**
 * A change to the guide makes a new version and keeps the old one: the new version's number, the
 * item it ends (a change), and the new item (with the one it replaces). Refused when the item
 * isn't in the current version or nothing about it changed.
 */
export function changeGuide(
  version: number,
  items: readonly ItemLike[],
  change: GuideChange,
): Read<{ version: number; ends: string | null; add: ItemInput & { replacesId: string | null } }> {
  const next = version + 1;
  if (change.kind === "add")
    return {
      ok: true,
      value: { version: next, ends: null, add: { ...change.item, replacesId: null } },
    };
  const was = guideAt(items, version).find((i) => i.id === change.itemId);
  if (!was) return no("That item has changed since. Open the guide again.");
  const same =
    was.section_id === change.item.sectionId &&
    was.body === change.item.body &&
    was.photo_file_id === change.item.photoFileId;
  if (same) return no("Nothing changed.");
  return {
    ok: true,
    value: { version: next, ends: was.id, add: { ...change.item, replacesId: was.id } },
  };
}

// ---- The review ----------------------------------------------------------------------------------------------

/** The review is due reviewDays after the last review (the guide's last change). YYYY-MM-DD. */
export const reviewDue = (lastReview: string, reviewDays: number) =>
  addDays(lastReview, reviewDays);

/**
 * The review date (brief 1E-18): the later of the guide's last change and the last "Reviewed, no
 * changes", plus reviewDays. YYYY-MM-DD.
 */
export function reviewDueFrom(
  lastChange: string,
  lastReviews: readonly string[],
  reviewDays: number,
): string {
  const latest = [lastChange, ...lastReviews].sort().at(-1) as string;
  return reviewDue(latest, reviewDays);
}

/** Whether the review is past due on a day. */
export const reviewOverdue = (today: string, due: string) => today > due;

// ---- Something changed --------------------------------------------------------------------------------------

export interface FlagLike {
  flagged_at: Date | null;
  cleared_at: Date | null;
}

/**
 * Something changed marks the item until the nurse changes it (a new version, which ends this
 * item) or keeps it. A later mark after a keep marks it again.
 */
export const isMarked = (i: FlagLike & { ended_version?: number | null }) =>
  !i.ended_version &&
  !!i.flagged_at &&
  (!i.cleared_at || i.cleared_at.getTime() < i.flagged_at.getTime());

/** The chip on an item the caregiver marked. */
export const MARKED_WORDS = "Changed? Check with the nurse";

// ---- Before you start ----------------------------------------------------------------------------------------

/**
 * Before you start shows when the caregiver has no checked-in visit with this patient before this
 * one, or the last was more than firstVisitDays ago. Days are the company's (YYYY-MM-DD).
 */
export function showsBeforeYouStart(
  priorCheckInDays: readonly string[],
  visitDay: string,
  firstVisitDays: number,
): boolean {
  const before = priorCheckInDays.filter((d) => d <= visitDay).sort();
  const last = before[before.length - 1];
  if (!last) return true;
  return addDays(last, firstVisitDays) < visitDay;
}

/** Before you start's line under its title. */
export const beforeWords = (first: string, firstVisitDays: number) =>
  `First time with ${first}, or more than ${firstVisitDays} days since the last visit.`;

export const HIGHLIGHTS_MAX = 4;

/**
 * Highlights are the guide's items with photos first, at most four, in section order (and the
 * order they were added within a section). Items in a section that's off never show.
 */
export function highlights<
  T extends Pick<ItemLike, "section_id" | "photo_file_id"> & { added_at: Date },
>(items: readonly T[], sections: readonly { id: string; position: number; is_on: boolean }[]): T[] {
  const place = new Map(sections.filter((s) => s.is_on).map((s) => [s.id, s.position]));
  const shown = items.filter((i) => place.has(i.section_id));
  const order = (a: T, b: T) =>
    (place.get(a.section_id) ?? 0) - (place.get(b.section_id) ?? 0) ||
    a.added_at.getTime() - b.added_at.getTime();
  return [
    ...shown.filter((i) => i.photo_file_id).sort(order),
    ...shown.filter((i) => !i.photo_file_id).sort(order),
  ].slice(0, HIGHLIGHTS_MAX);
}

// ---- The photo -----------------------------------------------------------------------------------------------

/** A patient photo needs photo consent when the setting is on. Why it's refused, or null. */
export function refusePhoto(
  consentOn: string | null,
  setting: Pick<HomeRoutineSetting, "photoConsentRequired">,
): string | null {
  return setting.photoConsentRequired && !consentOn ? "Tick Photo consent signed first." : null;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The square the photo is cropped to: around the face found (with room for the hair and chin),
 * kept inside the picture; with no face, the center square.
 */
export function cropToFace(face: Box | null, width: number, height: number): Box {
  const side = Math.min(width, height);
  if (!face || face.width <= 0 || face.height <= 0) {
    return {
      x: Math.round((width - side) / 2),
      y: Math.round((height - side) / 2),
      width: side,
      height: side,
    };
  }
  const size = Math.round(Math.min(side, Math.max(face.width, face.height) * 1.8));
  const cx = face.x + face.width / 2;
  const cy = face.y + face.height / 2;
  const clamp = (v: number, hi: number) => Math.round(Math.max(0, Math.min(v, hi)));
  return {
    x: clamp(cx - size / 2, width - size),
    y: clamp(cy - size / 2, height - size),
    width: size,
    height: size,
  };
}
